import { ChevronDown } from '@untitledui/icons/ChevronDown';
import { getExample, parsePhoneNumber } from 'awesome-phonenumber';
import { type ReactNode, useState } from 'react';

import { InputBase } from '@/components/base/input/input';
import { InputGroup } from '@/components/base/input/input-group';
import { COUNTRY_PHONE_CODES } from '@common/utils';

type CountryEntry = (typeof COUNTRY_PHONE_CODES)[number];
type CountryCode = CountryEntry['code'];

const COUNTRY_DIAL_CODES: Record<string, string> = Object.fromEntries(
  COUNTRY_PHONE_CODES.map(({ code, dialCode }) => [code, dialCode]),
);

const DIAL_CODE_TO_COUNTRY = [...COUNTRY_PHONE_CODES].toSorted((a, b) =>
  a.label.localeCompare(b.label),
);

const COUNTRY_OPTIONS = COUNTRY_PHONE_CODES.map(
  ({ code, label, dialCode }) => ({
    value: code,
    label: `${label} (${dialCode})`,
  }),
);

export interface PhoneNumberInputProps {
  label?: string;
  hint?: ReactNode;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  isInvalid?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  size?: 'xs' | 'sm' | 'md';
  tooltip?: string;
  className?: string;
  /** ISO country code to preselect when `value` carries no country of its own. */
  defaultCountry?: string;
}

export const PhoneNumberInput = ({
  label,
  hint,
  placeholder,
  value = '',
  onChange,
  onBlur,
  isInvalid,
  isDisabled,
  isRequired,
  size,
  tooltip,
  className,
  defaultCountry,
}: PhoneNumberInputProps) => {
  // Explicit dropdown choice, used only to disambiguate countries that share a
  // dial code (e.g. +1 for US/Canada). The stored `value` is the source of truth
  // otherwise, so the selector never drifts when `value` changes after mount.
  const [selectedCountry, setSelectedCountry] = useState<CountryCode | null>(
    null,
  );

  // Derive the country from `value` on every render. Prefer the library's region
  // resolution, which maps ambiguous NANP numbers to a real country (+1 → US by
  // default, CA/others by area code) rather than the alphabetically-first `+1`
  // entry; fall back to a raw dial-code match for partial input, then to an
  // explicit selection, then to `defaultCountry`, then to US.
  const parsedRegion = parsePhoneNumber(value).regionCode;
  const derivedCountry =
    COUNTRY_PHONE_CODES.find((entry) => entry.code === parsedRegion)?.code ??
    DIAL_CODE_TO_COUNTRY.find((entry) => value.startsWith(entry.dialCode))
      ?.code;
  const country =
    (selectedCountry &&
    value.startsWith(COUNTRY_DIAL_CODES[selectedCountry] ?? '\0')
      ? selectedCountry
      : derivedCountry) ??
    COUNTRY_PHONE_CODES.find((entry) => entry.code === defaultCountry)?.code ??
    'US';
  const dialCode = COUNTRY_DIAL_CODES[country] ?? '+1';

  const examplePlaceholder =
    placeholder ?? getExample(country).number?.national ?? '(555) 000-0000';

  // Stored value is `dialCode + digits`. Strip the dial code for display.
  const localDigits = (
    value.startsWith(dialCode) ? value.slice(dialCode.length) : value
  ).replaceAll(/\D/g, '');

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const entry = COUNTRY_PHONE_CODES.find((c) => c.code === e.target.value);
    if (!entry) return;
    setSelectedCountry(entry.code);
    onChange?.(entry.dialCode + localDigits);
  };

  const handleNumberChange = (newInput: string) => {
    // If the user typed/pasted a dial code (e.g. "+44 20 7946 0958"), switch country
    // and try to parse globally; otherwise parse in the current region so trunk
    // prefixes (e.g. UK "07500..." → "+447500...") get stripped on the way to E.164.
    if (newInput.startsWith('+')) {
      const match = DIAL_CODE_TO_COUNTRY.find((entry) =>
        newInput.startsWith(entry.dialCode),
      );
      if (match) {
        setSelectedCountry(match.code);
        const parsed = parsePhoneNumber(newInput);
        if (parsed.valid) {
          onChange?.(parsed.number.e164);
          return;
        }
        const restDigits = newInput
          .slice(match.dialCode.length)
          .replaceAll(/\D/g, '');
        onChange?.(match.dialCode + restDigits);
        return;
      }
    }

    const parsed = parsePhoneNumber(newInput, { regionCode: country });
    if (parsed.valid) {
      onChange?.(parsed.number.e164);
      return;
    }
    const digits = newInput.replaceAll(/\D/g, '');
    onChange?.(dialCode + digits);
  };

  return (
    <InputGroup
      label={label}
      hint={hint}
      isInvalid={isInvalid}
      isDisabled={isDisabled}
      isRequired={isRequired}
      size={size}
      className={className}
      value={localDigits}
      onChange={handleNumberChange}
      onBlur={onBlur}
      leadingAddon={
        <div className="relative mr-2 grid h-full items-center">
          <select
            aria-label="Country"
            value={country}
            onChange={handleCountryChange}
            disabled={isDisabled}
            className="h-full w-16 min-w-14 cursor-pointer appearance-none bg-inherit py-2 pr-5 pl-3 text-md text-transparent outline-hidden disabled:pointer-events-none disabled:cursor-not-allowed in-data-leading:rounded-r-none in-data-[input-size=md]:py-2.5 in-data-[input-size=md]:pl-3.5 in-data-[input-size=sm]:py-2 in-data-[input-size=sm]:pl-3"
          >
            {COUNTRY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute left-3 text-md text-tertiary group-disabled:text-disabled in-data-[input-size=md]:left-3.5">
            {dialCode}
          </span>
          <ChevronDown
            aria-hidden="true"
            className="pointer-events-none absolute right-0 size-4 stroke-[2.625px] text-fg-quaternary"
          />
        </div>
      }
    >
      <InputBase
        type="tel"
        placeholder={examplePlaceholder}
        tooltip={tooltip}
      />
    </InputGroup>
  );
};
